/* eslint-disable parog-web/no-glint-expect-error-directive */
import type { TOC } from '@ember/component/template-only';
import { concat, hash } from '@ember/helper';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import type { WithBoundArgs as WBA } from '@glint/template';
import { Changeset, type EmberChangeset } from 'ember-changeset';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import { t } from 'ember-intl';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import ForcBaseComponent from 'parog-web/components/forc/abstract/base-component';
import Provider from 'parog-web/components/forc/provider';
import type ConsultationFrequencyChangedJournalEvent from 'parog-web/models/consultation-frequency-changed-journal-event';
import ConsultationFrequency from 'parog-web/models/consultation/consultation-frequency';
import type MedicalActFrequencyChangedJournalEvent from 'parog-web/models/medical-act-frequency-changed-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';
import TypedEmberChangeset from 'parog-web/utils/typed-ember-changeset';
import ChangesetCheckbox from 'ui-kit/components/changeset/checkbox';
import ChangesetDropdown from 'ui-kit/components/changeset/dropdown';
import ChangesetLabeledTextArea from 'ui-kit/components/changeset/labeled-text-area';

export interface Args extends ForcBaseArgs {
  title?: string;
  consultationType?: string;
}

class ConsultAndMedical {
  constructor(
    public consultJE: ConsultationFrequencyChangedJournalEvent,
    public medicalJE: MedicalActFrequencyChangedJournalEvent
  ) {}

  async save() {
    const promises: Promise<unknown>[] = [this.consultJE.save()];
    if (this.medicalJE) promises.push(this.medicalJE.save());

    await Promise.all(promises);
  }
}

type BoundArgs = 'changeset' | 'isReadOnly';

interface ConditionalCommentableDropdownSig {
  Args: {
    shouldDisplayDropdownAndComment: boolean;
    changeset: EmberChangeset;
    isReadOnly: boolean | undefined;
  };
  Blocks: {
    default: [
      {
        Checkbox: WBA<typeof ChangesetCheckbox, BoundArgs>;
        DropdownPossibilities: WBA<typeof ChangesetDropdown, BoundArgs>;
        LabeledTextArea: WBA<typeof ChangesetLabeledTextArea, BoundArgs>;
      }
    ];
  };
}

const ConditionalCommentableDropdown = <template>
  <div class='two fields'>
    {{!-- prettier-ignore --}}
    <div class='field'>
      {{! @glint-expect-error }}
      {{yield
        (hash Checkbox=(component ChangesetCheckbox changeset=@changeset isReadOnly=@isReadOnly )) }}
    </div>

    {{#if @shouldDisplayDropdownAndComment}}
      {{!-- prettier-ignore --}}
      <div class='field'>
        {{! @glint-expect-error }}
        {{yield (hash DropdownPossibilities=(component ChangesetDropdown changeset=@changeset isReadOnly=@isReadOnly )) }}
      </div>
    {{/if}}
  </div>

  {{#if @shouldDisplayDropdownAndComment}}
    {{!-- prettier-ignore --}}
    <div class='field'>
      {{! @glint-expect-error }}
      {{yield (hash LabeledTextArea=(component ChangesetLabeledTextArea changeset=@changeset isReadOnly=@isReadOnly )) }}
    </div>
  {{/if}}
</template> satisfies TOC<ConditionalCommentableDropdownSig>;

interface ConsultSig {
  Args: {
    changeset: TypedEmberChangeset<ConsultAndMedical>;
    session: SemeiaSessionService;
    isReadOnly: boolean | undefined;
    consultationType?: string;
  };
}

interface MedicalActSig {
  Args: {
    changeset: TypedEmberChangeset<ConsultAndMedical>;
    session: SemeiaSessionService;
    isReadOnly: boolean | undefined;
  };
}

/** Whether or not a consultation frequency should be displayed
 *
 * @param filteringType optional filtering type
 * @param currentType the consultation frequency's type
 *
 * @returns true if filteringType is null, the comparison between filteringType & currentType otherwise
 */
function shouldDisplayConsultation(filteringType, currentType) {
  if (!filteringType) return true;

  return filteringType == currentType;
}

const Consultations = <template>
  <h2 class='ui primary header'>
    Consultations
  </h2>

  {{#each
    @changeset.data.consultJE.consultationFrequencies
    as |consultationFrequency index|
  }}
    {{#if
      (shouldDisplayConsultation @consultationType consultationFrequency.type)
    }}
      {{#if index}}
        <div class='ui section divider'></div>
      {{/if}}

      <ConditionalCommentableDropdown
        @shouldDisplayDropdownAndComment={{changesetGet
          @changeset
          (concat 'consultJE.consultationFrequencies.' index '.enabled')
        }}
        @changeset={{@changeset}}
        @isReadOnly={{@isReadOnly}}
        as |ccd|
      >
        <ccd.Checkbox
          @labelName={{t
            (concat
              'consultation-frequency.specialty.' consultationFrequency.key
            )
          }}
          @payloadKey='consultJE.consultationFrequencies.{{index}}.enabled'
          @dataTestInput={{concat consultationFrequency.key '-checkbox'}}
        />

        <ccd.DropdownPossibilities
          @propertyName='consultJE.consultationFrequencies.{{index}}.frequency'
          @labelName='Fréquence'
          @isMandatory={{true}}
          data-test-dropdown-possibility={{consultationFrequency.key}}
          as |dropdown|
        >
          {{#each
            ConsultationFrequency.validFrequencies
            as |dropdownPossibility|
          }}
            <dropdown.Item
              @key={{dropdownPossibility}}
              @value={{capitalize
                (t
                  (concat
                    'consultation-frequency.frequency.' dropdownPossibility
                  )
                )
              }}
            />
          {{/each}}
        </ccd.DropdownPossibilities>

        <ccd.LabeledTextArea
          @displaySubText={{if @session.medic true}}
          @subText='Ces informations sont partagées avec le patient.'
          @propertyName='consultJE.consultationFrequencies.{{index}}.comment'
          @labelName='Objectif'
          @placeholder='Vous pouvez ajouter un commentaire...'
          data-test-labeled-text-area={{consultationFrequency.key}}
        />
      </ConditionalCommentableDropdown>
    {{/if}}
  {{/each}}
</template> satisfies TOC<ConsultSig>;

const MedicalActs = <template>
  <h2 class='ui primary header'>
    Actes médicaux
  </h2>

  {{#each
    @changeset.data.medicalJE.medicalActFrequencies
    as |medicalActFrequency index|
  }}
    {{#if index}}
      <div class='ui section divider'></div>
    {{/if}}

    <ConditionalCommentableDropdown
      @shouldDisplayDropdownAndComment={{changesetGet
        @changeset
        (concat 'medicalJE.medicalActFrequencies.' index '.enabled')
      }}
      @changeset={{@changeset}}
      @isReadOnly={{@isReadOnly}}
      as |ccd|
    >
      <ccd.Checkbox
        @labelName={{medicalActFrequency.medicalAct.procedureSourceLabel}}
        @payloadKey='medicalJE.medicalActFrequencies.{{index}}.enabled'
        @dataTestInput={{concat
          medicalActFrequency.medicalAct.procedureSourceValue
          '-checkbox'
        }}
      />

      <ccd.DropdownPossibilities
        @propertyName='medicalJE.medicalActFrequencies.{{index}}.frequency'
        @labelName='Fréquence'
        @isMandatory={{true}}
        data-test-dropdown-possibility={{medicalActFrequency.medicalAct.procedureSourceValue}}
        as |dropdown|
      >
        {{#each
          ConsultationFrequency.validFrequencies
          as |dropdownPossibility|
        }}
          <dropdown.Item
            @key={{dropdownPossibility}}
            @value={{t
              (concat 'consultation-frequency.frequency.' dropdownPossibility)
            }}
          />
        {{/each}}
      </ccd.DropdownPossibilities>

      <ccd.LabeledTextArea
        @displaySubText={{if @session.medic true}}
        @subText='Ces informations sont partagées avec le patient.'
        @propertyName='medicalJE.medicalActFrequencies.{{index}}.comment'
        @labelName='Objectif'
        @placeholder='Vous
          pouvez ajouter un commentaire...'
        data-test-labeled-text-area={{medicalActFrequency.medicalAct.procedureSourceValue}}
      />
    </ConditionalCommentableDropdown>
  {{/each}}
</template> satisfies TOC<MedicalActSig>;

export default class ConsultationFrequencyEditionForm extends ForcBaseComponent<Args> {
  static fullComponentId = 'consultation-frequency/edition-form';

  @service declare session: SemeiaSessionService;

  declare changeset: TypedEmberChangeset<ConsultAndMedical>;

  override get isDirty(): boolean {
    return this.changeset.isDirty;
  }

  get title(): string {
    return this.args.title || 'Plan de suivi des consultations & examens';
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    this.changeset = Changeset(
      new ConsultAndMedical(
        args.patient.consultationFrequencyChangedJournalEvent,
        args.patient.medicalActFrequencyChangedJournalEvent
      ),
      () => true,
      {},
      { changeset: TypedEmberChangeset<ConsultAndMedical> }
    );
  }

  override async isValid(): Promise<boolean> {
    await this.changeset.validate();
    return this.changeset.isValid;
  }

  override async save(): Promise<void> {
    this.changeset.set('consultJE.source', 'wise');

    if (this.changeset.get('medicalJE')) {
      this.changeset.set('medicalJE.source', 'wise');
    }
    await this.changeset.save();
  }

  <template>
    {{! @glint-expect-error }}
    <Provider @forcComponent={{this}} @title={{this.title}}>
      <:content>
        <div class='mb-4'>
          <i class='exclamation triangle icon'></i>
          Si vous modifiez manuellement une information ici, le plan de suivi du
          patient ne sera plus actualisé automatiquement par la suite en
          fonction de son état de santé.
        </div>

        <Consultations
          @consultationType={{@consultationType}}
          @changeset={{this.changeset}}
          @session={{this.session}}
          @isReadOnly={{@isReadOnly}}
        />

        {{#if this.changeset.data.medicalJE}}
          <MedicalActs
            @changeset={{this.changeset}}
            @session={{this.session}}
            @isReadOnly={{@isReadOnly}}
          />
        {{/if}}
      </:content>
    </Provider>
  </template>
}
